<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>

    <form @submit.prevent="onSubmit">
      <div class="form-group">
        <label>{{ $t("models.market_plan.addForm.ruleType") }}</label>
        <CSelect
          class="bg-white"
          v-model="form.mode"
          :options="modeOptions"
          :reduce="(option) => option.value"
        />
      </div>

      <div class="form-group">
        <label>{{ $t("models.market_plan.addForm.ruleName") }}</label>
        <CInput v-model="form.name" type="text" />
      </div>

      <template v-if="form.mode === 'recurring'">
        <div class="form-group">
          <label>{{ $t("models.market_plan.addForm.frequency") }}</label>
          <CSelect
            class="bg-white"
            v-model="form.freq"
            :options="frequencyOptions"
            :reduce="(option) => option.value"
          />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.addForm.interval") }}</label>
          <CInput
            v-model.number="form.interval"
            type="number"
            min="1"
            placeholder="Every X occurrences"
          />
        </div>

        <div v-if="form.freq === RRule.WEEKLY" class="form-group">
          <label>{{ $t("models.market_plan.addForm.days") }}</label>
          <div class="d-flex flex-wrap" style="gap: 0.5rem">
            <CButton
              v-for="(day, index) in weekdays"
              :key="index"
              :color="isWeekdaySelected(day.value) ? 'primary' : 'gray-300'"
              size="sm"
              @click.prevent="toggleWeekday(day.value)"
            >
              {{ day.label }}
            </CButton>
          </div>
        </div>

        <div v-if="form.freq === RRule.MONTHLY" class="form-group">
          <label>{{ $t("models.market_plan.addForm.daysOfMonth") }}</label>
          <div class="d-flex flex-wrap" style="gap: 0.5rem">
            <CButton
              v-for="day in 31"
              :key="day"
              :color="isMonthdaySelected(day) ? 'primary' : 'gray-300'"
              size="sm"
              @click.prevent="toggleMonthday(day)"
            >
              {{ day }}
            </CButton>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.addForm.endDate") }}</label>
          <CInput v-model="form.until" type="date" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.order_close_time") }}</label>
          <CInput
            v-model="form.orderCloseTime"
            type="time"
            step="1"
            placeholder="HH:MM:SS"
          />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_producer_close") }}</label>
          <CInput v-model.number="form.offsetProducerClose" type="number" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_producer_delivery") }}</label>
          <CInput v-model.number="form.offsetProducerDelivery" type="number" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_order_delivery") }}</label>
          <CInput v-model.number="form.offsetOrderDelivery" type="number" />
        </div>
      </template>

      <template v-if="form.mode === 'specific'">
        <div class="form-group">
          <label>Select Dates</label>
          <div class="d-flex flex-wrap mb-2" style="gap: 0.5rem">
            <CButton
              v-for="(date, index) in form.specificDates"
              :key="index"
              color="primary"
              size="sm"
              @click.prevent="removeSpecificDate(index)"
            >
              {{ formatDate(date) }} <span class="ml-1">×</span>
            </CButton>
          </div>
          <CInput v-model="newDate" type="date" placeholder="Add a date" />
          <CButton
            color="primary"
            size="sm"
            class="mt-2"
            @click.prevent="addSpecificDate"
          >
            {{ $t("models.market_plan.addForm.addDate") }}
          </CButton>
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.order_close_time") }}</label>
          <CInput
            v-model="form.orderCloseTime"
            type="time"
            step="1"
            placeholder="HH:MM:SS"
          />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_producer_close") }}</label>
          <CInput v-model.number="form.offsetProducerClose" type="number" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_producer_delivery") }}</label>
          <CInput v-model.number="form.offsetProducerDelivery" type="number" />
        </div>

        <div class="form-group">
          <label>{{ $t("models.market_plan.offset_order_delivery") }}</label>
          <CInput v-model.number="form.offsetOrderDelivery" type="number" />
        </div>
      </template>
    </form>

    <CAlert v-if="validationError" color="danger" :show.sync="showAlert">
      {{ validationError }}
    </CAlert>

    <template #footer>
      <CButton color="primary" variant="outline" @click.prevent="closeModal">
        {{ $t("common.editor.cancel") }}
      </CButton>
      <vue-ladda
        :loading="loading"
        data-style="zoom-in"
        button-class="btn btn-primary px-4"
        @click.prevent="onSubmit"
      >
        {{ $t("common.editor.confirm") }}
      </vue-ladda>
    </template>
  </CModal>
</template>

<script>
import vSelect from "vue-select";
import { RRule, RRuleSet } from "rrule";
import { apiPatch, apiPost, apiDelete } from "../../../helpers/apiHelper";

export default {
  name: "PlanningModal",
  components: {
    vSelect,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: [Number, String],
      default: null,
    },
    values: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loading: false,
      RRule,
      newDate: null,
      validationError: null,
      showAlert: false,
      form: {
        mode: "recurring",
        name: null,
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [],
        bymonthday: [],
        until: null,
        specificDates: [],
        orderCloseTime: null,
        offsetProducerClose: 0,
        offsetProducerDelivery: 0,
        offsetOrderDelivery: 0,
      },
      modeOptions: [
        {
          value: "recurring",
          label: this.$t("models.market_plan.addForm.recurringLabel"),
        },
        {
          value: "specific",
          label: this.$t("models.market_plan.addForm.specificLabel"),
        },
      ],
      frequencyOptions: [
        {
          value: RRule.WEEKLY,
          label: this.$t("models.market_plan.addForm.weeklyLabel"),
        },
        {
          value: RRule.MONTHLY,
          label: this.$t("models.market_plan.addForm.monthlyLabel"),
        },
      ],
      weekdays: [
        { value: RRule.MO, label: this.$t("common.days.mon") },
        { value: RRule.TU, label: this.$t("common.days.tue") },
        { value: RRule.WE, label: this.$t("common.days.wed") },
        { value: RRule.TH, label: this.$t("common.days.thu") },
        { value: RRule.FR, label: this.$t("common.days.fri") },
        { value: RRule.SA, label: this.$t("common.days.sat") },
        { value: RRule.SU, label: this.$t("common.days.sun") },
      ],
    };
  },

  computed: {
    rruleString() {
      try {
        const parts = [];

        // Add recurring rule if in recurring mode
        if (this.form.mode === "recurring") {
          const options = {
            freq: this.form.freq,
            interval: this.form.interval || 1,
          };

          if (this.form.freq === RRule.WEEKLY && this.form.byweekday.length) {
            options.byweekday = this.form.byweekday;
          }

          if (this.form.freq === RRule.MONTHLY && this.form.bymonthday.length) {
            options.bymonthday = this.form.bymonthday;
          }

          if (this.form.until) {
            options.until = new Date(this.form.until);
          }

          const rrule = new RRule(options);
          parts.push(rrule.toString());
        }

        // Add specific dates (works for both modes)
        if (this.form.specificDates.length) {
          this.form.specificDates.forEach((dateString) => {
            const date = new Date(dateString);
            // Format date as RDATE:YYYYMMDDTHHmmssZ
            const formatted = date
              .toISOString()
              .replace(/[-:]/g, "")
              .replace(/\.\d{3}/, "");
            parts.push(`RDATE:${formatted}`);
          });
        }

        return parts.join("\n");
      } catch (e) {
        return "";
      }
    },
  },

  watch: {
    show: {
      handler(isShown) {
        if (isShown) {
          if (this.values && Object.keys(this.values).length > 0) {
            this.populateForm(this.values);
          } else {
            this.resetForm();
          }
        }
      },
      immediate: false,
    },
    values: {
      handler(newValues) {
        if (newValues && Object.keys(newValues).length > 0) {
          this.populateForm(newValues);
        }
      },
      immediate: true,
    },
  },

  methods: {
    populateForm(data) {
      this.form.name = data.name || null;
      this.form.orderCloseTime = data.order_close_time || null;
      this.form.offsetProducerClose = data.offset_producer_close || 0;
      this.form.offsetProducerDelivery = data.offset_producer_delivery || 0;
      this.form.offsetOrderDelivery = data.offset_order_delivery || 0;

      // Parse order_close to determine mode and populate form
      if (data.order_close) {
        const ruleString = data.order_close;

        if (ruleString.startsWith("RDATE:")) {
          // Specific dates mode
          this.form.mode = "specific";
          const lines = ruleString
            .split("\n")
            .filter((line) => line.startsWith("RDATE:"));
          this.form.specificDates = lines.map((line) => {
            const dateStr = line.replace("RDATE:", "");
            const date = new Date(
              dateStr.replace(
                /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
                "$1-$2-$3T$4:$5:$6Z"
              )
            );
            return date.toISOString().split("T")[0];
          });
        } else if (ruleString.startsWith("RRULE:")) {
          // Recurring mode
          this.form.mode = "recurring";
          const rruleStr = ruleString.split("\n")[0];

          // Parse RRULE string
          const freqMatch = rruleStr.match(/FREQ=(WEEKLY|MONTHLY)/);
          if (freqMatch) {
            this.form.freq =
              freqMatch[1] === "WEEKLY" ? RRule.WEEKLY : RRule.MONTHLY;
          }

          const intervalMatch = rruleStr.match(/INTERVAL=(\d+)/);
          if (intervalMatch) {
            this.form.interval = parseInt(intervalMatch[1]);
          }

          const byweekdayMatch = rruleStr.match(/BYDAY=([^;]+)/);
          if (byweekdayMatch) {
            const days = byweekdayMatch[1].split(",");
            const dayMap = {
              MO: RRule.MO,
              TU: RRule.TU,
              WE: RRule.WE,
              TH: RRule.TH,
              FR: RRule.FR,
              SA: RRule.SA,
              SU: RRule.SU,
            };
            this.form.byweekday = days.map((d) => dayMap[d]).filter(Boolean);
          }

          const bymonthdayMatch = rruleStr.match(/BYMONTHDAY=([^;]+)/);
          if (bymonthdayMatch) {
            this.form.bymonthday = bymonthdayMatch[1]
              .split(",")
              .map((d) => parseInt(d));
          }

          const untilMatch = rruleStr.match(/UNTIL=(\d{8}T\d{6}Z)/);
          if (untilMatch) {
            const dateStr = untilMatch[1];
            const date = new Date(
              dateStr.replace(
                /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
                "$1-$2-$3T$4:$5:$6Z"
              )
            );
            this.form.until = date.toISOString().split("T")[0];
          }
        }
      }
    },

    toggleWeekday(weekday) {
      const index = this.form.byweekday.indexOf(weekday);
      if (index > -1) {
        this.form.byweekday.splice(index, 1);
      } else {
        this.form.byweekday.push(weekday);
      }
    },

    isWeekdaySelected(weekday) {
      return this.form.byweekday.includes(weekday);
    },

    toggleMonthday(day) {
      const index = this.form.bymonthday.indexOf(day);
      if (index > -1) {
        this.form.bymonthday.splice(index, 1);
      } else {
        this.form.bymonthday.push(day);
      }
    },

    isMonthdaySelected(day) {
      return this.form.bymonthday.includes(day);
    },

    addSpecificDate() {
      if (this.newDate && !this.form.specificDates.includes(this.newDate)) {
        this.form.specificDates.push(this.newDate);
        this.form.specificDates.sort();
        this.newDate = null;
      }
    },

    removeSpecificDate(index) {
      this.form.specificDates.splice(index, 1);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    async onSubmit() {
      // Clear previous validation error
      this.validationError = null;
      this.showAlert = false;

      // Validation
      if (!this.form.name) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.name"
        );
        this.showAlert = true;
        return;
      }

      if (!this.form.orderCloseTime) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.closeTime"
        );
        this.showAlert = true;
        return;
      }

      if (
        this.form.offsetProducerClose === null ||
        this.form.offsetProducerClose === undefined
      ) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.producerCloseOffset"
        );
        this.showAlert = true;
        return;
      }

      if (
        this.form.offsetProducerDelivery === null ||
        this.form.offsetProducerDelivery === undefined
      ) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.producerDeliveryOffset"
        );
        this.showAlert = true;
        return;
      }

      if (
        this.form.offsetOrderDelivery === null ||
        this.form.offsetOrderDelivery === undefined
      ) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.orderDeliveryOffset"
        );
        this.showAlert = true;
        return;
      }

      // Mode-specific validation
      if (this.form.mode === "recurring") {
        if (
          this.form.freq === RRule.WEEKLY &&
          this.form.byweekday.length === 0
        ) {
          this.validationError = this.$t(
            "models.market_plan.addForm.validation.recurring"
          );
          this.showAlert = true;
          return;
        }

        if (
          this.form.freq === RRule.MONTHLY &&
          this.form.bymonthday.length === 0
        ) {
          this.validationError = this.$t(
            "models.market_plan.addForm.validation.daysMonth"
          );
          this.showAlert = true;
          return;
        }
      } else if (this.form.mode === "specific") {
        if (this.form.specificDates.length === 0) {
          this.validationError = this.$t(
            "models.market_plan.addForm.validation.specificDate"
          );
          this.showAlert = true;
          return;
        }
      }

      const requestParams = {
        name: this.form.name,
        order_close_time: this.form.orderCloseTime,
        order_close: this.rruleString,
        offset_producer_close: this.form.offsetProducerClose,
        offset_producer_delivery: this.form.offsetProducerDelivery,
        offset_order_delivery: this.form.offsetOrderDelivery,
      };

      // check user permissions before submitting
      const perm = this.$store.state.connections.current.permissions.role_lg;
      if (perm !== 2) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.permissions"
        );
        this.showAlert = true;
        return;
      }

      let response;
      if (this.editMode && this.editId) {
        response = await apiPatch(
          "role_market_planningdata",
          this.editId,
          requestParams,
          [this.$store.state.role.id]
        );
      } else {
        response = await apiPost("role_market_planningdata", requestParams, [
          this.$store.state.role.id,
        ]);
      }

      if (response) {
        this.$emit("submitted");
        this.closeModal();
      }
    },

    closeModal() {
      this.$emit("update:show", false);
      this.validationError = null;
      this.showAlert = false;
      this.newDate = null;
    },

    resetForm() {
      this.form = {
        mode: "recurring",
        name: null,
        freq: RRule.WEEKLY,
        interval: 1,
        byweekday: [],
        bymonthday: [],
        until: null,
        specificDates: [],
        orderCloseTime: null,
        offsetProducerClose: null,
        offsetProducerDelivery: null,
        offsetOrderDelivery: null,
      };
      this.validationError = null;
      this.showAlert = false;
      this.newDate = null;
    },
  },
};
</script>
